import React, { useState, useEffect } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { Form, Input, Button, Typography, Flex, Alert, message, Card, Avatar, Space, Checkbox, Tabs } from 'antd'
import { EyeInvisibleOutlined, EyeTwoTone, ClearOutlined, ToolOutlined } from '@ant-design/icons'
import { useAuth } from '../contexts/AuthContext'
import { useTheme } from '../contexts/ThemeContext'
import ThemeToggle from '../components/ThemeToggle'
import AppIcon from '../components/AppIcon'
import LoginRomaneio from '../components/LoginRomaneio'
import '../styles/global-input-fixes.css'
import { isAndroid, openConfig } from '../utils/androidUtils'
import pkg from '../../package.json'


const { Title, Text } = Typography

// Chave usada para persistir o código do usuário
const USER_CODE_STORAGE_KEY = 'login.codigo'
// Chave usada para persistir o domínio do ERP
const DOMAIN_STORAGE_KEY = 'login.dominio'
// Chave usada para persistir o usuário quando "Lembrar Código" está ativo
const USERNAME_STORAGE_KEY = 'login.usuario'
// Chave para saber se o "Lembrar Código" está marcado
const REMEMBER_CODE_KEY = 'login.lembrarCodigo'

// Função para traduzir mensagens de erro para português
const translateErrorMessage = (errorMessage) => {
  const errorTranslations = {
    'Invalid credentials': 'Email ou senha incorretos. Verifique suas credenciais e tente novamente.',
    'User already exists': 'Este email já está cadastrado no sistema.',
    'Authentication failed': 'Falha na autenticação. Faça login novamente.',
    'Usuário inativo. Entre em contato com o administrador.': 'Sua conta está inativa. Entre em contato com o administrador para reativar seu acesso.',
    'Usuário não encontrado ou inativo': 'Sua conta está inativa ou não foi encontrada. Entre em contato com o administrador.',
    'Network Error': 'Erro de conexão. Verifique sua internet e tente novamente.',
    'HTTP error! status: 500': 'Erro interno do servidor. Tente novamente em alguns minutos.',
    'HTTP error! status: 404': 'Serviço não encontrado. Verifique se o servidor está funcionando.',
    'HTTP error! status: 400': 'Dados inválidos. Verifique as informações inseridas.',
    'Failed to fetch': 'Não foi possível conectar ao servidor. Verifique sua conexão com a internet.'
  }

  // Verifica se existe uma tradução específica
  if (errorTranslations[errorMessage]) {
    return errorTranslations[errorMessage]
  }

  // Verifica se a mensagem contém alguma das chaves conhecidas
  for (const [key, translation] of Object.entries(errorTranslations)) {
    if (errorMessage.includes(key)) {
      return translation
    }
  }

  // Se não encontrar tradução, retorna uma mensagem genérica amigável
  return 'Ocorreu um erro inesperado. Tente novamente ou entre em contato com o suporte.'
}

const Login = () => {
  const [form] = Form.useForm()
  const [clientImage, setClientImage] = useState(null)
  const [imageLoaded, setImageLoaded] = useState(false)
  const [clientImageSize, setClientImageSize] = useState(null)
  const [formLogoLoaded, setFormLogoLoaded] = useState(false)
  const [formLogoSrc, setFormLogoSrc] = useState('/cliente.png')
  const { login, isLoading, error, clearError } = useAuth()
  const { currentTheme } = useTheme()
  const isDark = currentTheme === 'dark'
  const navigate = useNavigate()
  const location = useLocation()

  // Função para limpar cache
  const clearCache = () => {
    // Limpar localStorage
    localStorage.clear();

    // Limpar sessionStorage
    sessionStorage.clear();

    // Limpar cookies (apenas os que podem ser acessados via JavaScript)
    document.cookie.split(";").forEach(function (c) {
      document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
    });

    // Tentar limpar cache do service worker se disponível
    if ('caches' in window) {
      caches.keys().then(function (names) {
        for (let name of names) {
          caches.delete(name);
        }
      });
    }

    // Desregistrar service workers se houver
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then(function (registrations) {
        for (let registration of registrations) {
          registration.unregister();
        }
      });
    }

    alert('Cache limpo com sucesso! A página será recarregada.');
    window.location.reload();
  };

  // Responsive styles
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768)

  // Lê os valores salvos no localStorage para popular os campos ao iniciar.
  // Domínio é sempre restaurado; código e usuário apenas se "Lembrar Código" estiver ativo.
  const initialFormValues = (() => {
    try {
      const savedCodigo = localStorage.getItem(USER_CODE_STORAGE_KEY)
      const rememberCode = localStorage.getItem(REMEMBER_CODE_KEY) !== 'false'
      const savedDominio = localStorage.getItem(DOMAIN_STORAGE_KEY)
      const savedUsuario = localStorage.getItem(USERNAME_STORAGE_KEY)
      const fields = {}
      if (savedCodigo) fields.codigo = savedCodigo
      if (savedDominio) fields.dominio = savedDominio
      if (savedUsuario) fields.usuario = savedUsuario
      if (rememberCode) fields.lembrarCodigo = true
      return fields
    } catch (_) {
      // Ignora erros de acesso ao localStorage
      return {}
    }
  })()

  // Restaurar o domínio e demais valores salvos assim que o form for montado,
  // garantindo que o input de domínio já nasça preenchido.
  useEffect(() => {
    try {
      if (Object.keys(initialFormValues).length) {
        // Aguarda o form registrar os campos (evita race condition com campos em Tabs)
        setTimeout(() => form.setFieldsValue(initialFormValues), 0)
      }
    } catch (_) {
      // Ignora erros de acesso ao form
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form])

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768)
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Clear any previous errors when component mounts
  useEffect(() => {
    clearError()
  }, [])

  // Impede scrollbar no body enquanto estiver no login (tooltip do botão de tema gerava overflow)
  useEffect(() => {
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prevOverflow
    }
  }, [])

  // Check for custom client image
  useEffect(() => {
    const checkClientImage = async () => {
      const imagePath = import.meta.env.VITE_CLIENT_LOGIN_IMAGE

      if (imagePath) {
        try {
          // Create a new image to test if it loads
          const img = new Image()
          img.onload = () => {
            setClientImage(imagePath)
            setImageLoaded(true)
            // Captura dimensões naturais para cálculo de proporção
            setClientImageSize({ width: img.naturalWidth, height: img.naturalHeight })
          }
          img.onerror = () => {
            console.warn('Client image not found or failed to load:', imagePath)
            setImageLoaded(false)
          }
          img.src = imagePath
        } catch (error) {
          console.warn('Error loading client image:', error)
          setImageLoaded(false)
        }
      }
    }

    checkClientImage()
  }, [])

  // Carregar dinamicamente o logo do cliente (subdomínio -> VITE_CLIENT_LOGIN_IMAGE -> /cliente.png)
  useEffect(() => {
    let isMounted = true

    const resolveLogo = async () => {
      // Extrai subdomínio (ex: cld.etiquetando.app.br -> cld)
      const getSubdomain = () => {
        try {
          const hostname = window.location.hostname.toLowerCase()
          if (/^(?:\d{1,3}\.){3}\d{1,3}$/.test(hostname) || hostname === 'localhost') {
            return null
          }
          const parts = hostname.split('.')
          if (parts.length > 1 && parts[parts.length - 1] === 'localhost') {
            return parts[0] !== 'www' ? parts[0] : null
          }
          if (parts.length >= 3 && parts[0] !== 'www') {
            return parts[0]
          }
        } catch {
          return null
        }
        return null
      }

      const testImage = (src) => {
        return new Promise((resolve) => {
          if (!src) return resolve(false)
          const img = new Image()
          img.onload = () => resolve(true)
          img.onerror = () => resolve(false)
          img.src = src
        })
      }

      // Torna fundo branco ou quase branco transparente via Canvas
      const removeWhiteBackground = (imageSrc, tolerance = 35) => {
        return new Promise((resolve) => {
          if (!imageSrc) return resolve(imageSrc)
          const img = new Image()
          img.crossOrigin = 'Anonymous'
          img.onload = () => {
            try {
              const canvas = document.createElement('canvas')
              const width = img.naturalWidth || img.width
              const height = img.naturalHeight || img.height
              canvas.width = width
              canvas.height = height
              const ctx = canvas.getContext('2d')
              if (!ctx) return resolve(imageSrc)

              ctx.drawImage(img, 0, 0)
              const imgData = ctx.getImageData(0, 0, width, height)
              const data = imgData.data

              // Verifica os 4 cantos para ver se a imagem tem fundo branco sólido
              const cornerIndices = [
                0,
                (width - 1) * 4,
                ((height - 1) * width) * 4,
                ((height - 1) * width + (width - 1)) * 4
              ]

              let whiteCorners = 0
              for (const idx of cornerIndices) {
                const r = data[idx]
                const g = data[idx + 1]
                const b = data[idx + 2]
                const a = data[idx + 3]
                if (a > 200 && r >= 255 - tolerance && g >= 255 - tolerance && b >= 255 - tolerance) {
                  whiteCorners++
                }
              }

              // Se pelo menos 2 cantos forem brancos, remove os tons de branco
              if (whiteCorners >= 2) {
                for (let i = 0; i < data.length; i += 4) {
                  const r = data[i]
                  const g = data[i + 1]
                  const b = data[i + 2]
                  const a = data[i + 3]

                  if (a > 0 && r >= 255 - tolerance && g >= 255 - tolerance && b >= 255 - tolerance) {
                    data[i + 3] = 0 // Torna o pixel transparente
                  }
                }
                ctx.putImageData(imgData, 0, 0)
                resolve(canvas.toDataURL('image/png'))
              } else {
                resolve(imageSrc)
              }
            } catch {
              resolve(imageSrc)
            }
          }
          img.onerror = () => resolve(imageSrc)
          img.src = imageSrc
        })
      }

      const origin = window.location.origin
      const subdomain = getSubdomain()
      const versionParam = `?v=${pkg?.version || '1.0.16'}`
      const candidates = []

      // 1. Prioridade: logo com nome do subdomínio (.png, .jpeg, .jpg)
      if (subdomain) {
        candidates.push(`${origin}/${subdomain}.png${versionParam}`)
        candidates.push(`/${subdomain}.png${versionParam}`)
        candidates.push(`${origin}/${subdomain}.jpeg${versionParam}`)
        candidates.push(`/${subdomain}.jpeg${versionParam}`)
        candidates.push(`${origin}/${subdomain}.jpg${versionParam}`)
        candidates.push(`/${subdomain}.jpg${versionParam}`)
      }

      // 2. Prioridade: imagem configurada na variável de ambiente
      const envImage = import.meta.env.VITE_CLIENT_LOGIN_IMAGE
      if (envImage && envImage.trim()) {
        const trimmed = envImage.trim()
        const fullUrl = trimmed.startsWith('http') ? trimmed : `${origin}${trimmed.startsWith('/') ? '' : '/'}${trimmed}`
        candidates.push(`${fullUrl}${fullUrl.includes('?') ? '&' : '?'}v=${pkg?.version || '1.0.16'}`)
      }

      // 3. Fallback padrão: /cliente.png
      candidates.push(`${origin}/cliente.png${versionParam}`)
      candidates.push(`/cliente.png${versionParam}`)

      console.log('[Logo Tenant] Subdomínio:', subdomain, '| Testando candidatos:', candidates)

      for (const path of candidates) {
        const exists = await testImage(path)
        console.log(`[Logo Tenant] Teste de imagem [${path}]:`, exists ? 'ENCONTRADA' : 'NÃO ENCONTRADA')
        if (exists && isMounted) {
          const finalSrc = await removeWhiteBackground(path)
          if (isMounted) {
            setFormLogoSrc(finalSrc)
            setFormLogoLoaded(true)
          }
          return
        }
      }

      if (isMounted) {
        setFormLogoLoaded(false)
      }
    }

    resolveLogo()

    return () => {
      isMounted = false
    }
  }, [])

  const handleSubmit = async (values) => {
    try {
      // Remove caracteres . e - do campo código antes de enviar
      const cleanedValues = {
        ...values,
        codigo: values.codigo ? values.codigo.replace(/[.-]/g, '') : values.codigo
      }

      // Memoriza o domínio sempre.
      if (cleanedValues.dominio) {
        localStorage.setItem(DOMAIN_STORAGE_KEY, cleanedValues.dominio)
      }
      // "Lembrar Código": persiste ou remove o código e o usuário.
      if (cleanedValues.lembrarCodigo) {
        localStorage.setItem(USER_CODE_STORAGE_KEY, cleanedValues.codigo || '')
        localStorage.setItem(USERNAME_STORAGE_KEY, cleanedValues.usuario || '')
        localStorage.setItem(REMEMBER_CODE_KEY, 'true')
      } else {
        localStorage.removeItem(USER_CODE_STORAGE_KEY)
        localStorage.removeItem(USERNAME_STORAGE_KEY)
        localStorage.setItem(REMEMBER_CODE_KEY, 'false')
      }

      const result = await login(cleanedValues)

      if (result.success) {
        // O redirecionamento será feito automaticamente pelo PublicRoute baseado no role do usuário
        // Não fazemos redirecionamento manual aqui
      } else {
        // O erro já foi capturado pelo contexto de autenticação
        // e será exibido automaticamente pelo componente Alert
        console.error('Login failed:', result.error)
        // Alert personalizado para informar o erro ao usuário
        message.error({
          content: translateErrorMessage(result.error),
          duration: 5,
          style: {
            marginTop: '20vh',
          },
        })
      }
    } catch (error) {
      // Captura erros não tratados pelo contexto
      console.error('Unexpected login error:', error)
    }
  }

  const handleFieldChange = (changedFields, allFields) => {
    // Limpa erro quando o usuário começa a digitar
    if (error) {
      clearError()
    }

    // "Lembrar Código" só persiste o código/usuário se marcado; o envio também gerencia.
    try {
      const lembrar = localStorage.getItem(REMEMBER_CODE_KEY) ?? 'true'
      if (lembrar === 'false') {
        localStorage.removeItem(USER_CODE_STORAGE_KEY)
        localStorage.removeItem(USERNAME_STORAGE_KEY)
      }
    } catch (_) {
      // Ignora erros de acesso ao localStorage
    }
  }

  return (
    <div
      className="login-container"
      style={{
        height: '100vh',
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        background: isDark ? '#000' : '#f6ffed',
        margin: 0,
        padding: 0,
        overflow: 'hidden',
        position: 'relative'
      }}
    >
      {/* Botío de ferramenta para Android - posicionado no topo direito */}
      {isAndroid() && (
        <Button
          type="text"
          icon={<ToolOutlined />}
          onClick={openConfig}
          style={{
            position: 'absolute',
            top: '16px',
            right: '64px',
            zIndex: 1000,
            color: isDark ? '#fff' : '#666',
            border: 'none',
            backgroundColor: isDark ? 'rgba(30, 41, 59, 0.9)' : 'rgba(255, 255, 255, 0.9)',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
          }}
          title="Ferramentas"
        />
      )}

      {/* Left Side - Image Section */}
      {isMobile ? (
        <div
          className="login-left-section-mobile"
          style={{
            background: isDark
              ? 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)'
              : 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            overflow: 'hidden',
            minHeight: '30vh',
            flex: isMobile ? '0 0 auto' : 'none'
          }}
        >
          {/* Imagem do cliente renderizada com proporções mínimas de 500px */}
          {clientImage && imageLoaded && clientImageSize && (
            <img
              src={clientImage}
              alt="Imagem do Cliente"
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                objectFit: 'contain',
                zIndex: 0,
                // Regras de tamanho: paisagem -> height 500, largura automática; retrato -> width 500; quadrada -> 500x500
                ...(clientImageSize.width > clientImageSize.height
                  ? { height: '500px', width: 'auto' }
                  : clientImageSize.height > clientImageSize.width
                    ? { width: '500px', height: 'auto' }
                    : { width: '500px', height: '500px' }),
                // Evitar overflow em telas menores
                maxWidth: '95vw',
                maxHeight: '70vh',
                borderRadius: '12px'
              }}
            />
          )}
          <div
            className="login-left-content"
            style={{
              textAlign: 'center',
              color: 'white',
              zIndex: 1,
              padding: 'clamp(20px, 5vw, 40px)'
            }}
          >
            <div style={{ marginBottom: '30px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <AppIcon
                size={isMobile ? 64 : 48}
                style={{
                  display: 'block',
                  minWidth: isMobile ? '64px' : '48px',
                  minHeight: isMobile ? '64px' : '48px',
                  maxWidth: isMobile ? '80px' : '60px',
                  maxHeight: isMobile ? '80px' : '60px',
                  filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))'
                }}
              />
            </div>
            <Title level={2} style={{ color: 'white', marginBottom: '8px', fontSize: 'clamp(56px, 20vw, 88px)' }}>EtiquetandoApp</Title>


          </div>
        </div>
      ) : (
        <div
          className="login-left-section"
          style={{
            flex: 1,
            background: isDark
              ? 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)'
              : 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            display: 'flex',
            position: 'relative',
            overflow: 'hidden',
            minHeight: '100vh',
            padding: '40px 32px'
          }}
        >
          {/* Imagem do cliente renderizada com proporções mínimas de 500px */}
          {clientImage && imageLoaded && clientImageSize && (
            <img
              src={clientImage}
              alt="Imagem do Cliente"
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                objectFit: 'contain',
                zIndex: 0,
                ...(clientImageSize.width > clientImageSize.height
                  ? { height: '500px', width: 'auto' }
                  : clientImageSize.height > clientImageSize.width
                    ? { width: '500px', height: 'auto' }
                    : { width: '500px', height: '500px' }),
                maxWidth: '80vw',
                maxHeight: '80vh',
                borderRadius: '12px'
              }}
            />
          )}
          <div
            className="login-left-content"
            style={{
              width: '100%',
              minHeight: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              alignItems: 'center',
              color: 'white',
              zIndex: 1,
              padding: 'clamp(20px, 5vw, 40px)'
            }}
          >
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center' }}>
              <div style={{ marginBottom: '30px' }}>
                <AppIcon size={80} />
              </div>
              <Title level={1} style={{ color: 'white', marginBottom: '16px', fontSize: 'clamp(32px, 6vw, 48px)' }}>EtiquetandoApp</Title>
            </div>

            <Flex justify="center" align="center" style={{ gap: 12, flexWrap: 'wrap' }}>
              <Link
                to="/privacidade"
                style={{
                  color: 'rgba(255, 255, 255, 0.8)',
                  fontSize: '12px',
                  textDecoration: 'underline'
                }}
              >
                Privacidade
              </Link>
              <Link
                to="/termos"
                style={{
                  color: 'rgba(255, 255, 255, 0.8)',
                  fontSize: '12px',
                  textDecoration: 'underline'
                }}
              >
                Termos
              </Link>
            </Flex>
          </div>
        </div>
      )}

      {/* Right Side - Login Form */}
      <div
        className="login-right-section"
        style={{
          flex: isMobile ? 'none' : 1,
          width: isMobile ? '100%' : 'auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: isMobile ? '10px' : '20px',
          overflowY: 'auto',
          background: isDark ? '#111111' : 'white',
          minHeight: isMobile ? '70vh' : '100vh'
        }}
      >
        <div
          className="login-form-container"
          style={{
            width: '100%',
            maxWidth: '400px',
            padding: isMobile ? '5px 16px 16px 16px' : '30px',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            borderRadius: '12px',
            boxShadow: isDark ? '0 8px 32px rgba(255, 255, 255, 0.1)' : '0 8px 32px rgba(0, 0, 0, 0.1)',
            background: isDark ? '#1a1a1a' : 'white',
            border: isDark ? '1px solid #434343' : '1px solid #f0f0f0'
          }}
        >
          {/* Logo do cliente acima do formulário */}
          {formLogoLoaded && (
            <div style={{ textAlign: 'center', marginBottom: '17px' }}>
              <img
                src={formLogoSrc}
                alt="Logo Cliente"
                style={{
                  maxWidth: '128px',
                  maxHeight: '128px',
                  width: 'auto',
                  height: 'auto',
                  objectFit: 'contain',
                  filter: isDark
                    ? 'drop-shadow(0 0 1px rgba(255, 255, 255, 0.4)) drop-shadow(0 0 6px rgba(255, 255, 255, 0.15))'
                    : 'none'
                }}
                onError={(e) => {
                  // Hide the image if it fails to load
                  e.target.style.display = 'none'
                }}
              />
            </div>
          )}

          {/* Tabs: Entrar (login normal) | Romaneio (impressão On-Board) */}
          <Tabs
            defaultActiveKey="login"
            centered
            size="small"
            items={[
              {
                key: 'login',
                label: 'Entrar',
                children: (
                  <>
                    {/* Error Display */}
                    {error && (
                      <>
                        <Alert
                          message="Erro de Login"
                          description={translateErrorMessage(error)}
                          type="error"
                          showIcon
                          style={{ marginBottom: '24px', borderRadius: '8px' }}
                        />
                      </>
                    )}




                    {/* Login Form */}
                    <Form
                      form={form}
                      layout="vertical"
                      onFinish={handleSubmit}
                      onFieldsChange={handleFieldChange}
                      autoComplete="off"
                    >
                      {/* Domínio + Código lado a lado */}
                      <Flex gap={isMobile ? 8 : 12}>
                        <Form.Item
                          label={<span style={{ color: isDark ? '#fff' : '#333', fontWeight: '500' }}>Domínio</span>}
                          name="dominio"
                          rules={[
                            {
                              required: true,
                              message: 'Por favor, insira o domínio do ERP!',
                            },
                          ]}
                          style={{ flex: 1, minWidth: 0, marginBottom: isMobile ? '12px' : '16px' }}
                        >
                          <Input
                            placeholder=""
                            autoComplete="off"
                            style={{
                              height: isMobile ? '35px' : '45px',
                              borderRadius: '8px',
                              fontSize: '14px',
                              border: isDark ? '1px solid #444' : '1px solid #d9d9d9',
                              width: '100%',
                              boxSizing: 'border-box',
                              backgroundColor: isDark ? '#1e293b' : '#fff',
                              color: isDark ? '#ffffff' : '#000000',
                              WebkitTextFillColor: isDark ? '#ffffff' : '#000000'
                            }}
                          />
                        </Form.Item>

                        {/* Código Field (ex-CPF) */}
                        <Form.Item
                          label={<span style={{ color: isDark ? '#fff' : '#333', fontWeight: '500' }}>Código</span>}
                          name="codigo"
                          style={{ flex: 1, minWidth: 0, marginBottom: isMobile ? '12px' : '16px' }}
                        >
                          <Input
                            placeholder=""
                            autoComplete="off"
                            style={{
                              height: isMobile ? '35px' : '45px',
                              borderRadius: '8px',
                              fontSize: '14px',
                              border: isDark ? '1px solid #444' : '1px solid #d9d9d9',
                              width: '100%',
                              boxSizing: 'border-box',
                              backgroundColor: isDark ? '#1e293b' : '#fff',
                              color: isDark ? '#ffffff' : '#000000',
                              WebkitTextFillColor: isDark ? '#ffffff' : '#000000'
                            }}
                          />
                        </Form.Item>
                      </Flex>

                      {/* Usuário Field */}
                      <Form.Item
                        label={<span style={{ color: isDark ? '#fff' : '#333', fontWeight: '500' }}>Usuário</span>}
                        name="usuario"
                        rules={[
                          {
                            required: true,
                            message: 'Por favor, insira seu usuário!',
                          },
                        ]}
                        style={{ marginBottom: isMobile ? '12px' : '16px' }}
                      >
                        <Input
                          placeholder="Digite seu usuário"
                          autoComplete="codigo"
                          style={{
                            height: isMobile ? '35px' : '45px',
                            borderRadius: '8px',
                            fontSize: '14px',
                            border: isDark ? '1px solid #444' : '1px solid #d9d9d9',
                            width: '100%',
                            boxSizing: 'border-box',
                            backgroundColor: isDark ? '#1e293b' : '#fff',
                            color: isDark ? '#ffffff' : '#000000',
                            WebkitTextFillColor: isDark ? '#ffffff' : '#000000'
                          }}
                        />
                      </Form.Item>

                      {/* Password Field */}
                      <Form.Item
                        label={<span style={{ color: isDark ? '#fff' : '#333', fontWeight: '500' }}>Senha</span>}
                        name="password"
                        rules={[
                          {
                            required: true,
                            message: 'Por favor, insira sua senha!',
                          },
                        ]} style={{ marginBottom: isMobile ? '12px' : '16px' }}
                      >
                        <Input.Password
                          placeholder="••••••••••"
                          autoComplete="current-password"
                          style={{
                            height: isMobile ? '40px' : '45px',
                            borderRadius: '8px',
                            fontSize: '14px',
                            border: isDark ? '1px solid #444' : '1px solid #d9d9d9',
                            backgroundColor: isDark ? '#1e293b' : '#fff',
                            color: isDark ? '#fff' : '#000',
                            background: 'inherit'
                          }}
                          iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                        />
                      </Form.Item>

                      {/* Lembrar Código */}
                      <Form.Item name="lembrarCodigo" valuePropName="checked" style={{ marginBottom: isMobile ? '4px' : '6px' }}>
                        <Checkbox style={{ color: isDark ? '#fff' : '#333' }}>Lembrar Código</Checkbox>
                      </Form.Item>


                      {/* Submit Button */}
                      <Form.Item style={{ marginBottom: isMobile ? '4px' : '6px' }}>
                        <Button
                          type="primary"
                          htmlType="submit"
                          block
                          loading={isLoading}
                          style={{
                            height: isMobile ? '40px' : '45px',
                            borderRadius: '8px',
                            fontSize: '16px',
                            fontWeight: '600'
                          }}
                        >
                          Entrar
                        </Button>
                      </Form.Item>
                    </Form>
                  </>
                ),
              },
              {
                key: 'romaneio',
                label: 'Romaneio',
                children: <LoginRomaneio />,
              },
            ]}
          />

          {/* Clear Cache Button - Discreto no footer (desktop) */}
          {!isMobile && (
            <Flex justify="center" style={{ marginTop: '8px' }}>
              <Button
                type="text"
                icon={<ClearOutlined />}
                onClick={clearCache}
                size="small"
                style={{
                  color: isDark ? 'rgba(255, 255, 255, 0.45)' : 'rgba(0, 0, 0, 0.45)',
                  border: 'none',
                  fontSize: '12px',
                  height: 'auto',
                  padding: '4px 8px'
                }}
                title="Limpar Cache"
              >
                Limpar cache
              </Button>
            </Flex>
          )}

          {/* Footer mobile: Privacidade | Limpar cache | Termos */}
          {isMobile && (
            <Flex
              align="center"
              style={{
                marginTop: '8px',
                width: '100%',
                gap: 8
              }}
            >
              <div style={{ flex: 1, textAlign: 'left' }}>
                <Link
                  to="/privacidade"
                  style={{
                    color: isDark ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.4)',
                    fontSize: '11px',
                    textDecoration: 'underline'
                  }}
                >
                  Privacidade
                </Link>
              </div>
              <div style={{ flex: 1, textAlign: 'center' }}>
                <Button
                  type="text"
                  icon={<ClearOutlined />}
                  onClick={clearCache}
                  size="small"
                  style={{
                    color: isDark ? 'rgba(255, 255, 255, 0.45)' : 'rgba(0, 0, 0, 0.45)',
                    border: 'none',
                    fontSize: '12px',
                    height: 'auto',
                    padding: '4px 8px'
                  }}
                  title="Limpar Cache"
                >
                  Limpar cache
                </Button>
              </div>
              <div style={{ flex: 1, textAlign: 'right' }}>
                <Link
                  to="/termos"
                  style={{
                    color: isDark ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.4)',
                    fontSize: '11px',
                    textDecoration: 'underline'
                  }}
                >
                  Termos de Uso
                </Link>
              </div>
            </Flex>
          )}

          {/* App Version */}
          <Flex justify="center" style={{ marginTop: '4px' }}>
            <Text
              style={{
                color: isDark ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
                fontSize: '11px'
              }}
            >
              v{pkg?.version || '1.0.0'}
            </Text>
          </Flex>
        </div>


      </div>

      {/* Botío de alternar tema */}
      <ThemeToggle
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          zIndex: 1000,
          color: isDark ? '#fff' : '#666',
          backgroundColor: isDark ? 'rgba(30, 41, 59, 0.9)' : 'rgba(255, 255, 255, 0.9)',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          width: '32px',
          height: '32px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      />


    </div>
  )
}

export default Login
